if (!customElements.get('header-sub-menu')) {
  class HeaderSubMenu extends HTMLElement {
    constructor() {
      super();
      this.desktopMedia = window.matchMedia('(min-width: 1200px)');
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverCloseTimer = null;
      this.panelAnimationFrames = new Map();
      this.panelCloseTimers = new Map();
      this.openBlockId = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-sub-menu-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-sub-menu-panel]'));
      this.headerWrapper = this.closest('.header-wrapper');
      this.highlightParentheticalText();

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          const blockId = trigger.dataset.subMenuTrigger;
          if (this.desktopMedia.matches && event.detail > 0) {
            this.open(blockId);
          } else {
            this.toggle(blockId);
          }
        });
        trigger.addEventListener('mouseenter', () => {
          if (this.desktopMedia.matches) this.open(trigger.dataset.subMenuTrigger);
        });
      });

      this.querySelectorAll('[data-sub-menu-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeAll({ restoreFocus: true }));
      });

      this.addEventListener('mouseenter', () => window.clearTimeout(this.hoverCloseTimer));
      this.addEventListener('mouseleave', () => {
        if (!this.desktopMedia.matches) return;
        this.hoverCloseTimer = window.setTimeout(() => this.closeAll(), 160);
      });
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);

      this.headerWrapper?.querySelector('.header__inline-menu')?.addEventListener('mouseenter', () => this.closeAll());
      this.headerWrapper?.querySelector('.header__inline-menu')?.addEventListener('focusin', () => this.closeAll());

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.desktopMedia.addEventListener('change', this.onBreakpointChange);
    }

    highlightParentheticalText() {
      const parentheticalPattern = /(\([^()]*\)|（[^（）]*）)/g;

      this.querySelectorAll('.header-sub-menu__link').forEach((link) => {
        const text = link.textContent;
        const matches = Array.from(text.matchAll(parentheticalPattern));

        if (!matches.length) return;

        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach((match) => {
          if (match.index > lastIndex) {
            fragment.append(document.createTextNode(text.slice(lastIndex, match.index)));
          }

          const parenthetical = document.createElement('span');
          parenthetical.className = 'header-sub-menu__parenthetical';
          parenthetical.textContent = match[0];
          fragment.append(parenthetical);
          lastIndex = match.index + match[0].length;
        });

        if (lastIndex < text.length) {
          fragment.append(document.createTextNode(text.slice(lastIndex)));
        }

        link.replaceChildren(fragment);
      });
    }

    disconnectedCallback() {
      window.clearTimeout(this.hoverCloseTimer);
      this.panels?.forEach((panel) => this.cancelPanelAnimation(panel));
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.desktopMedia.removeEventListener('change', this.onBreakpointChange);
    }

    toggle(blockId) {
      if (this.openBlockId === blockId) {
        this.closeAll({ restoreFocus: true });
      } else {
        this.open(blockId);
      }
    }

    open(blockId) {
      const trigger = this.triggers.find((item) => item.dataset.subMenuTrigger === blockId);
      const panel = this.panels.find((item) => item.dataset.subMenuPanel === blockId);
      if (!trigger || !panel) return;

      if (this.openBlockId === blockId && panel.classList.contains('is-open')) return;

      this.closeMainNavigation();
      this.openBlockId = blockId;
      this.triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      this.panels.forEach((item) => {
        if (item === panel) {
          this.openPanel(item, blockId);
        } else {
          this.closePanel(item);
        }
      });
      this.headerWrapper?.classList.add('sub-menu-open');
      this.dispatchEvent(new CustomEvent('header-sub-menu:toggle', { bubbles: true, detail: { open: true } }));
    }

    closeAll({ restoreFocus = false, immediate = false } = {}) {
      if (!this.triggers || !this.panels) return;
      const activeTrigger = this.triggers.find((item) => item.dataset.subMenuTrigger === this.openBlockId);

      this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      this.openBlockId = null;
      this.panels.forEach((item) => this.closePanel(item, { immediate }));
      this.headerWrapper?.classList.remove('sub-menu-open');

      if (restoreFocus) activeTrigger?.focus();
      this.dispatchEvent(new CustomEvent('header-sub-menu:toggle', { bubbles: true, detail: { open: false } }));
    }

    openPanel(panel, blockId) {
      this.cancelPanelAnimation(panel);
      const wasHidden = panel.hidden;

      panel.hidden = false;
      panel.classList.remove('is-closing');

      if (wasHidden) {
        panel.classList.remove('is-open');
        panel.getBoundingClientRect();
      }

      const animationFrame = window.requestAnimationFrame(() => {
        this.panelAnimationFrames.delete(panel);
        if (this.openBlockId === blockId && !panel.hidden) panel.classList.add('is-open');
      });
      this.panelAnimationFrames.set(panel, animationFrame);
    }

    closePanel(panel, { immediate = false } = {}) {
      if (panel.hidden || (panel.classList.contains('is-closing') && !immediate)) return;

      this.cancelPanelAnimation(panel);
      panel.classList.remove('is-open');
      panel.classList.add('is-closing');

      const finishClosing = () => {
        this.panelCloseTimers.delete(panel);
        if (panel.classList.contains('is-open')) return;
        panel.hidden = true;
        panel.classList.remove('is-closing');
      };

      if (immediate || this.reducedMotionMedia.matches) {
        finishClosing();
        return;
      }

      const closeTimer = window.setTimeout(finishClosing, 520);
      this.panelCloseTimers.set(panel, closeTimer);
    }

    cancelPanelAnimation(panel) {
      const animationFrame = this.panelAnimationFrames.get(panel);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      this.panelAnimationFrames.delete(panel);

      const closeTimer = this.panelCloseTimers.get(panel);
      if (closeTimer) window.clearTimeout(closeTimer);
      this.panelCloseTimers.delete(panel);
    }

    closeMainNavigation() {
      this.headerWrapper?.querySelectorAll('header-menu details[open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });

      const drawer = this.headerWrapper?.querySelector('header-drawer');
      const drawerDetails = drawer?.querySelector(':scope > details[open]');
      if (drawerDetails && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(new Event('click'), drawerDetails.querySelector('summary'));
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeAll();
    }

    onKeyDown(event) {
      if (event.key !== 'Escape' || !this.openBlockId) return;
      event.preventDefault();
      this.closeAll({ restoreFocus: true });
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closeAll();
      });
    }

    onBreakpointChange() {
      this.closeAll({ immediate: true });
    }

    onBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (blockId && this.panels.some((panel) => panel.dataset.subMenuBlockId === blockId)) {
        this.open(blockId);
      }
    }

    onBlockDeselect(event) {
      if (event.detail?.blockId === this.openBlockId) this.closeAll();
    }
  }

  customElements.define('header-sub-menu', HeaderSubMenu);
}
